import * as React from 'react';
import { Import } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { Button } from '@ooh/ui/components/button';
import { PermissionDeniedState } from '@ooh/ui/components/permission-denied-state';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@ooh/ui/components/dropdown-menu';

interface LoadingSkeletonProps {
  testId?: string;
}

export function LoadingSkeleton({ testId = 'skeleton-loading' }: LoadingSkeletonProps) {
  return (
    <div className="p-6" data-testid={testId}>
      <div className="flex gap-2 mb-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-8 w-28 rounded-full bg-muted animate-pulse" />
        ))}
      </div>
      <div className="mb-4 h-12 rounded-lg border border-border bg-muted/10 animate-pulse" />
      <div className="rounded-lg border border-border overflow-hidden">
        {Array.from({ length: 10 }).map((_, i) => (
          // eslint-disable-next-line react/no-array-index-key -- static skeleton rows have no stable key
          <div key={i} className="h-12 border-b border-border/50 bg-muted/20 animate-pulse" />
        ))}
      </div>
    </div>
  );
}

interface UnauthorizedStateProps {
  message: string;
  testId?: string;
}

/** Fallback screen shown in place of the catalog when the caller lacks `media_unit.view`. */
export function UnauthorizedState({
  message,
  testId = 'media-units-permission-denied',
}: UnauthorizedStateProps) {
  return <PermissionDeniedState message={message} testId={testId} />;
}

interface ImportMenuProps {
  canImport?: boolean;
  canImportAvailability?: boolean;
  importMenuLabel?: string;
  importMediaLabel?: string;
  importAvailabilityLabel?: string;
}

/**
 * Single consolidated "Import…" entry point for the inventory toolbar. Each
 * menu item is independently gated on its own permission; the trigger itself
 * renders only when at least one permission is held.
 */
export function ImportMenu({
  canImport = false,
  canImportAvailability = false,
  importMenuLabel = '',
  importMediaLabel = '',
  importAvailabilityLabel = '',
}: ImportMenuProps) {
  const navigate = useNavigate();
  if (!canImport && !canImportAvailability) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="outline" data-testid="import-menu-trigger">
          <Import className="h-3.5 w-3.5" />
          {importMenuLabel}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {canImport ? (
          <DropdownMenuItem
            data-testid="import-media-units-menu-item"
            onSelect={() => {
              void navigate({ to: '/media/import' });
            }}
          >
            {importMediaLabel}
          </DropdownMenuItem>
        ) : null}
        {canImportAvailability ? (
          <DropdownMenuItem
            data-testid="import-availability-menu-item"
            onSelect={() => {
              void navigate({ to: '/availability-import' });
            }}
          >
            {importAvailabilityLabel}
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
